/**
 * Tour Step Definitions
 *
 * Pure data describing each step for player and GM feature tours.
 * Steps with a `spotlight` selector highlight that element; otherwise the slide is centered.
 */

export interface TourStep {
  id: string;
  title: string;
  description: string;
  /** CSS selector for the element to spotlight (via data-tour attributes) */
  spotlight?: string;
  /** Path to screenshot image (optional — shows placeholder if missing) */
  image?: string;
}

export interface TourTrack {
  id: string;
  name: string;
  steps: TourStep[];
}

// ── Player Tour ──────────────────────────────────────────────────────────────

const PLAYER_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome, Adventurer',
    description:
      'Machina is a virtual tabletop for playing D&D with friends. Your Game Master has prepared a world — let us show you around.',
    image: '/assets/tour/welcome-bg.png',
  },
  {
    id: 'game-board',
    title: 'The Game Board',
    description:
      'This is your battlefield and exploration space. Click and drag to pan the map, use the scroll wheel to zoom in and out.',
    spotlight: '[data-tour="map-canvas"]',
    image: '/assets/tour/game-board.png',
  },
  {
    id: 'party-panel',
    title: 'Your Party',
    description:
      'Your character sheet, ability checks, and HP tracking live here. Click your character to expand the full sheet.',
    spotlight: '[data-tour="party-panel"]',
    image: '/assets/tour/party-panel.png',
  },
  {
    id: 'chat-window',
    title: 'Team Chat',
    description:
      'Talk with your party, roll dice with /roll commands, and look up spells with /spell. All rolls appear here for everyone to see.',
    spotlight: '[data-tour="chat-window"]',
    image: '/assets/tour/chat-window.png',
  },
  {
    id: 'toolbar',
    title: 'Your Tools',
    description:
      'Select tokens, measure distances, and roll dice from the toolbar. Drag it anywhere you like.',
    spotlight: '[data-tour="toolbar"]',
    image: '/assets/tour/toolbar.png',
  },
  {
    id: 'initiative',
    title: 'Combat',
    description:
      'When battle begins, the initiative tracker shows turn order. Click to roll initiative and track who acts next.',
    spotlight: '[data-tour="initiative"]',
    image: '/assets/tour/initiative.png',
  },
  {
    id: 'ready',
    title: "You're Ready!",
    description:
      'That covers the essentials. Your GM will guide the rest. Good luck out there — may your rolls be ever natural 20s.',
  },
];

// ── GM-Only Steps (appended after player steps, before closing) ──────────────

const GM_EXTRA_STEPS: TourStep[] = [
  {
    id: 'gm-tray',
    title: 'GM Tray',
    description:
      'Your command center. Six tabs cover everything: party context, combat controls, world management, story sessions, a sound board, and AI assistance.',
    spotlight: '[data-tour="gm-tray"]',
    image: '/assets/tour/gm-tray.png',
  },
  {
    id: 'gm-closing',
    title: 'Command the Table',
    description:
      'Machina\'s AI assists with monster tactics, NPC dialogue, and session pacing — but you always have the final say. Open the GM Console from the header to begin.',
  },
];

// ── Assembled Tracks ─────────────────────────────────────────────────────────

export const PLAYER_TOUR: TourTrack = {
  id: 'player-intro',
  name: 'Welcome to Machina',
  steps: PLAYER_STEPS,
};

export const GM_TOUR: TourTrack = {
  id: 'gm-intro',
  name: 'Welcome to Machina (GM)',
  // All player steps (minus the closing "ready" step), then GM steps, then a GM closing
  steps: [
    ...PLAYER_STEPS.slice(0, -1), // Remove player closing
    ...GM_EXTRA_STEPS,
    {
      id: 'gm-ready',
      title: "You're Ready, Game Master",
      description:
        'The table is yours. Your players are counting on you — no pressure. Open the GM Console whenever you need it.',
    },
  ],
};

export function getTourById(tourId: string): TourTrack | undefined {
  if (tourId === 'player-intro') return PLAYER_TOUR;
  if (tourId === 'gm-intro') return GM_TOUR;
  return undefined;
}
